/**
 * Слежение за версией интерфейса.
 *
 * Код обновляется выкладкой на сервер, а вкладка может жить сутками: SPA не
 * перезагружается сама, Safari вдобавок восстанавливает страницу из снимка
 * сессии. В итоге человек работает в прошлой сборке и видит исправленные
 * ошибки как живые — а по переписке это неотличимо от «не починено».
 *
 * Поэтому приложение спрашивает у сервера свою версию: при возврате в
 * вкладку и раз в десять минут. Файл /build.json кладётся рядом со сборкой и
 * не кэшируется. Если версия разошлась — показываем полосу «вышла новая
 * версия», перезагрузку человек делает сам: посреди правки перезагружать
 * нельзя, черновик в поле ввода пропадёт.
 */
import { useEffect, useState } from 'react'

const CHECK_INTERVAL_MS = 10 * 60 * 1000

async function serverBuild(): Promise<string | null> {
  try {
    const res = await fetch(`/build.json?t=${performance.now().toFixed(0)}`, { cache: 'no-store' })
    if (!res.ok) return null
    const data = (await res.json()) as { build?: unknown }
    return typeof data.build === 'string' ? data.build : null
  } catch {
    /* Нет сети или сервер недоступен — молчим: это не повод тревожить. */
    return null
  }
}

/** true, когда на сервере лежит сборка новее загруженной. */
export function useFreshBuild(current: string): boolean {
  const [stale, setStale] = useState(false)

  useEffect(() => {
    let stopped = false

    const check = async (): Promise<void> => {
      if (stopped || stale) return
      const server = await serverBuild()
      if (!stopped && server !== null && server !== current) setStale(true)
    }

    const onVisible = (): void => { if (document.visibilityState === 'visible') void check() }

    void check()
    const timer = window.setInterval(() => void check(), CHECK_INTERVAL_MS)
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    /* Safari достаёт страницу из кэша «назад/вперёд» без единого запроса —
       событие pageshow с persisted единственный способ это заметить. */
    window.addEventListener('pageshow', onVisible)

    return () => {
      stopped = true
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
      window.removeEventListener('pageshow', onVisible)
    }
  }, [current, stale])

  return stale
}
